/**
 * Data Fetch Drawer Component
 *
 * Displays customer bank/FI account details in a drawer
 * Shows accounts grouped by FIP ID with collapsible accordion view
 */

import React, { useState } from 'react';
import { Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay } from 'equal-ds-ui';
import type { DataFetchDrawerProps } from '../../../types/data-fetch-request';

/**
 * Collapsible FIP account group card
 */
const FIPAccountCard: React.FC<{
  fipId: string;
  accounts: Array<{
    masked_account_number: string;
    account_type: string;
    fi_type: string;
    link_reference_number: string;
  }>;
  defaultExpanded?: boolean;
}> = ({ fipId, accounts, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-border-default rounded-lg mb-3 bg-white shadow-sm">
      {/* FIP Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-background-secondary transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary-500" />
          <span className="font-semibold text-text-primary">FIP ID: {fipId}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-text-secondary" />
        ) : (
          <ChevronDown className="w-5 h-5 text-text-secondary" />
        )}
      </button>

      {/* Accounts List */}
      {isExpanded && (
        <div className="px-4 py-3 space-y-4 bg-background-tertiary">
          {accounts.map((account, index) => (
            <div key={index} className="border-l-2 border-primary-300 pl-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-700">{index + 1}</span>
                </div>

                <div className="flex-1 space-y-2">
                  {/* Masked Account Number */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      Masked Account Number
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary font-mono">
                      {account.masked_account_number || 'N/A'}
                    </div>
                  </div>

                  {/* Account Type */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      Account Type
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary">
                      {account.account_type || 'N/A'}
                    </div>
                  </div>

                  {/* FI Type */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      FI Type
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary">
                      {account.fi_type || 'N/A'}
                    </div>
                  </div>

                  {/* Linked Reference Number */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      Linked Reference Number
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary font-mono">
                      {account.link_reference_number || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider between accounts (except last) */}
              {index < accounts.length - 1 && (
                <hr className="mt-4 border-border-light" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DataFetchDrawer: React.FC<DataFetchDrawerProps> = ({
  selectedRequest,
  fipAccountGroups,
}) => {
  // Note: isOpen and onClose are managed by DrawerProvider in parent
  if (!selectedRequest) {
    return null;
  }

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="60%">
        <DrawerContent className="equal-ds-root w-full flex flex-col flex-1">
          <DrawerHeader className="border-b border-border-primary pb-4">
            <h2 className="text-xl font-semibold text-text-primary">
              Customer Bank/FI Account Details
            </h2>
          </DrawerHeader>

          <div className="p-6 flex-1 overflow-y-auto">
            {fipAccountGroups.length === 0 ? (
              <div className="text-center py-8 text-text-secondary">
                No account details available
              </div>
            ) : (
              <div className="space-y-2">
                {fipAccountGroups.map((group) => (
                  <FIPAccountCard
                    key={group.fip_id}
                    fipId={group.fip_id}
                    accounts={group.accounts}
                  />
                ))}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
